// src/pages/AdminVendorsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import { Upload, Save, RefreshCw, Trash2, Edit3, Check } from "lucide-react";

function InputCell({ value, onChange, disabled }) {
  return (
    <input
      className="input"
      disabled={disabled}
      value={value ?? ""}   // allow empty
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", minWidth: 140 }}
    />
  );
}

const LABELS_KEY = "vendors_col_labels_v1";

export default function AdminVendorsPage() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);

  const [cols, setCols] = useState([]); // [{name,type,...}]
  const [colLabels, setColLabels] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(LABELS_KEY) || "{}");
    } catch {
      return {};
    }
  });
  const [editHeaders, setEditHeaders] = useState(false);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [file, setFile] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    sessionStorage.setItem(LABELS_KEY, JSON.stringify(colLabels || {}));
  }, [colLabels]);

  const loadSchema = async () => {
    try {
      const { data } = await api.get("/vendors/schema"); // ✅ no /api prefix
      const columns = Array.isArray(data?.columns) ? data.columns : [];
      setCols(columns);
    } catch (e) {
      setCols([]);
      setMsg(e?.response?.data?.error || "Failed to load schema");
    }
  };

  const load = async () => {
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.get("/vendors", { params: { q, limit: 500 } }); // ✅ no /api prefix
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setCount(Number(data?.count ?? 0));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchema();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({ ...r });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.put(`/vendors/${editingId}`, draft); // ✅ no /api prefix
      setRows((prev) => prev.map((x) => (x.id === editingId ? data : x)));
      cancelEdit();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const delRow = async (id) => {
    if (!window.confirm(`Delete vendor #${id}?`)) return;
    setLoading(true);
    setMsg("");
    try {
      await api.delete(`/vendors/${id}`); // ✅ no /api prefix
      setRows((prev) => prev.filter((x) => x.id !== id));
      setCount((c) => Math.max(0, c - 1));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/vendors/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }); // ✅ no /api prefix

      setMsg(`Imported ${data?.imported ?? 0} vendors.`);
      setFile(null);

      // After import, schema and rows can change (new columns)
      await loadSchema();
      await load();

      // Ensure UI exits edit mode if it was editing a now-stale row
      cancelEdit();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => rows, [rows]);

  // Renderable columns: put id first, hide nothing except id duplication
  const renderCols = useMemo(() => {
    const names = cols.map((c) => c.name);
    const filtered = names.filter((n) => n !== "id");
    return ["id", ...filtered];
  }, [cols]);

  return (
    <div className="container space-y">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Vendors</h2>
          <div className="muted" style={{ marginTop: 4 }}>{count} records</div>
        </div>

        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 320 }}
          />
          <button className="btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={16} style={{ marginRight: 8 }} />
            Refresh
          </button>

          <button
            className={editHeaders ? "btn" : "btn-outline"}
            onClick={() => setEditHeaders((v) => !v)}
            disabled={loading}
            title="Rename headers (UI labels only)"
          >
            {editHeaders ? <Check size={16} style={{ marginRight: 8 }} /> : <Edit3 size={16} style={{ marginRight: 8 }} />}
            {editHeaders ? "Done headers" : "Edit headers"}
          </button>
        </div>
      </div>

      {msg ? <div className="muted">{msg}</div> : null}

      <div className="card">
        <div style={{ fontWeight: 900, marginBottom: 10 }}>CSV Import</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button className="btn" onClick={doImport} disabled={!file || loading}>
            <Upload size={16} style={{ marginRight: 8 }} />
            Import
          </button>
          <div className="muted" style={{ fontSize: 12 }}>
            Headers are auto-detected from the first CSV row. After import, you can rename header labels (UI only).
          </div>
        </div>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {renderCols.map((c) => (
                <th
                  key={c}
                  style={{
                    textAlign: "left",
                    padding: 10,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                    opacity: 0.9,
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom",
                  }}
                >
                  {c === "id" ? (
                    "id"
                  ) : editHeaders ? (
                    <input
                      className="input"
                      value={colLabels[c] ?? c}
                      onChange={(e) => setColLabels((m) => ({ ...m, [c]: e.target.value }))}
                      style={{ width: "100%", minWidth: 140 }}
                    />
                  ) : (
                    colLabels[c] ?? c
                  )}
                </th>
              ))}
              <th
                style={{
                  textAlign: "left",
                  padding: 10,
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                  opacity: 0.9,
                  whiteSpace: "nowrap",
                }}
              >
                actions
              </th>
            </tr>
          </thead>

          <tbody>
            {visible.map((r) => {
              const editing = editingId === r.id;
              const row = editing ? draft : r;

              return (
                <tr key={r.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  {renderCols.map((c) => (
                    <td key={c} style={{ padding: 10, whiteSpace: c === "id" ? "nowrap" : "normal" }}>
                      {c === "id" ? (
                        `#${r.id}`
                      ) : (
                        <InputCell
                          disabled={!editing}
                          value={row?.[c] ?? ""}   // ✅ robust for missing keys
                          onChange={(v) => setDraft((d) => ({ ...d, [c]: v }))}
                        />
                      )}
                    </td>
                  ))}

                  <td style={{ padding: 10, whiteSpace: "nowrap" }}>
                    {!editing ? (
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn-outline" onClick={() => startEdit(r)} disabled={loading}>
                          Edit
                        </button>
                        <button className="btn-outline" onClick={() => delRow(r.id)} disabled={loading} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn" onClick={saveEdit} disabled={loading}>
                          <Save size={16} style={{ marginRight: 8 }} />
                          Save
                        </button>
                        <button className="btn-outline" onClick={cancelEdit} disabled={loading}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {visible.length === 0 ? (
              <tr>
                <td colSpan={renderCols.length + 1} className="muted" style={{ padding: 12 }}>
                  No vendors.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}